import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Upload, CheckCircle, ArrowLeft, FileCsv, Clock, Database } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import AdminNav from './AdminNav';
import EnrichmentStatus from './EnrichmentStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInventory = () => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadHistory, setUploadHistory] = useState(null);
  const [currentCount, setCurrentCount] = useState(0);

  useEffect(() => {
    loadUploadHistory();
  }, []);

  const loadUploadHistory = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }
      const response = await axios.get(`${API_URL}/api/admin/inventory/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploadHistory(response.data.history);
      setCurrentCount(response.data.current_count);
    } catch (error) {
      console.error('Failed to load upload history:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      setUploading(true);
      setUploadResult(null);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/admin/inventory/upload`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResult(response.data);
      toast.success(`Inventory uploaded: ${response.data.count} items`);
      // Refresh history after successful upload
      await loadUploadHistory();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload inventory';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setUploading(false);
      // Reset the file input
      e.target.value = '';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#05050A]">
      <AdminNav title="Inventory Upload" titleTestId="inventory-upload-title" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Current Inventory Status */}
        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Database size={32} className="text-[#10B981]" />
              <div>
                <h3 className="text-lg font-['JetBrains_Mono'] font-bold text-[#F8FAFC]">
                  Current Inventory
                </h3>
                <p className="text-[#94A3B8] text-sm">
                  {uploadHistory && uploadHistory.length > 0 
                    ? `Last updated: ${new Date(uploadHistory[0].uploaded_at).toLocaleString()}`
                    : 'No uploads yet'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-['JetBrains_Mono'] font-bold text-[#10B981]" data-testid="current-count">
                {currentCount.toLocaleString()}
              </p>
              <p className="text-[#94A3B8] text-sm">items</p>
            </div>
          </div>
        </div>

        <EnrichmentStatus />

        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-8">
          <div className="text-center mb-8">
            <FileCsv size={64} className="mx-auto text-[#007AFF] mb-4" />
            <h2 className="text-2xl font-['JetBrains_Mono'] font-bold mb-2">
              Upload Daily Inventory
            </h2>
            <p className="text-[#94A3B8]">
              Upload an Excel file with inventory data. Expected columns: Name, Description, RFQ, QtyOnHand, QtyIncoming, Condition, TAA
            </p>
          </div>

          {/* Upload Area */}
          <div className="border-2 border-dashed border-[#334155] rounded-lg p-12 text-center hover:border-[#007AFF] transition-colors">
            <Upload size={48} className="mx-auto text-[#94A3B8] mb-4" />
            <p className="text-[#F8FAFC] font-['JetBrains_Mono'] mb-4">
              Drop your Excel file here or click to browse
            </p>
            <input
              data-testid="file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button
                data-testid="upload-btn"
                asChild
                disabled={uploading}
                className="bg-[#0047AB] hover:bg-[#005DF0] font-['JetBrains_Mono'] cursor-pointer"
              >
                <span>
                  {uploading ? 'Uploading...' : 'Select File'}
                </span>
              </Button>
            </label>
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div className="mt-8 bg-[#10B981]/10 border border-[#10B981] rounded-lg p-6" data-testid="upload-success">
              <div className="flex items-start gap-4">
                <CheckCircle size={32} className="text-[#10B981] flex-shrink-0" weight="fill" />
                <div>
                  <h3 className="text-lg font-['JetBrains_Mono'] font-bold text-[#10B981] mb-2">
                    Upload Successful!
                  </h3>
                  <p className="text-[#F8FAFC] mb-2">
                    {uploadResult.message}
                  </p>
                  <p className="text-sm text-[#94A3B8] font-['JetBrains_Mono']">
                    Total items: {uploadResult.count}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="mt-8 bg-[#1E293B]/30 rounded-lg p-6">
            <h3 className="font-['JetBrains_Mono'] font-bold text-[#007AFF] mb-4">
              File Format Instructions
            </h3>
            <ul className="space-y-2 text-[#94A3B8] text-sm">
              <li className="flex items-start gap-2">
                <span className="text-[#007AFF] font-bold">•</span>
                <span>File must be in .xlsx or .xls format</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#007AFF] font-bold">•</span>
                <span>Required columns: Name (Part Number), Description, RFQ, QtyOnHand, QtyIncoming, Condition, TAA</span>
              </li>
              <li className="text-[#94A3B8] text-sm">
                <span>Optional column: UnitPrice (for admin quote reference - not shown to customers)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#007AFF] font-bold">•</span>
                <span>Condition values: F/S (Factory Sealed), REF (Refurbished), New</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#007AFF] font-bold">•</span>
                <span>TAA values: Yes or No (TAA Compliant)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#007AFF] font-bold">•</span>
                <span>You can name the file with current date: e.g., 032526EE1RFQ.xlsx</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#007AFF] font-bold">•</span>
                <span>Uploading a new file will replace the current active inventory</span>
              </li>
            </ul>
          </div>

          <div className="mt-8 flex justify-between">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-[#334155] font-['JetBrains_Mono']"
              data-testid="back-dashboard-btn"
            >
              Back to Dashboard
            </Button>
            <Button
              onClick={() => navigate('/inventory')}
              variant="outline"
              className="border-[#007AFF] text-[#007AFF] font-['JetBrains_Mono']"
              data-testid="view-inventory-btn"
            >
              View Public Inventory
            </Button>
          </div>
        </div>

        {/* Upload History */}
        {uploadHistory && uploadHistory.length > 0 && (
          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6 mt-6">
            <h3 className="text-lg font-['JetBrains_Mono'] font-bold text-[#F8FAFC] mb-4 flex items-center gap-2">
              <Clock size={24} className="text-[#007AFF]" />
              Upload History
            </h3>
            <div className="space-y-3">
              {uploadHistory.map((upload, index) => (
                <div 
                  key={upload.id || index}
                  className="flex items-center justify-between p-3 bg-[#1E293B]/30 rounded-lg"
                  data-testid={`upload-history-${index}`}
                >
                  <div>
                    <p className="font-['JetBrains_Mono'] text-[#F8FAFC] text-sm">
                      {upload.filename}
                    </p>
                    <p className="text-[#94A3B8] text-xs">
                      {new Date(upload.uploaded_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-['JetBrains_Mono'] text-[#10B981] text-sm">
                      {upload.item_count?.toLocaleString()} items
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInventory;
